import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CardComp } from "../../../../components";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import {
  KycReviewStatusBadge,
  ParticipantTypeBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import KycMatchIndicator, { FieldMatchIndicator } from "../../../../components/Admin/InvestorPortal/KycMatchIndicator";
import KycReviewTimeline from "../../../../components/Admin/InvestorPortal/KycReviewTimeline";
import InvestorPortalDetailHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailHeader";
import InvestorPortalActionButton from "../../../../components/Admin/InvestorPortal/InvestorPortalActionButton";
import {
  DetailSection,
  DetailField,
  DetailFieldList,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailFields";
import {
  useGetStaffInvestorKycQuery,
  useApproveStaffInvestorKycMutation,
  useRejectStaffInvestorKycMutation,
  useUnderReviewStaffInvestorKycMutation,
  useRequestResubmissionStaffInvestorKycMutation,
  useAddStaffInvestorKycNoteMutation,
} from "../../../../store/admin/investorKyc/staffInvestorKycApi";
import { getKycReviewActions } from "../../../../store/admin/investorOperations/kycLifecycle";
import { KYC_REVIEW_STATUS } from "../../../../store/admin/investorOperations/constants";
import { mapStaffKycDetail } from "../../../../utils/staffKycAdapter";
import moment from "moment";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

function resolveApiAction(action) {
  if (action?.action === "reject") return "reject";
  if (action?.action === "resubmit") return "resubmit";
  if (action?.target === KYC_REVIEW_STATUS.APPROVED) return "approve";
  if (action?.target === KYC_REVIEW_STATUS.UNDER_REVIEW) return "under_review";
  return null;
}

function KycReviewDetailPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [noteText, setNoteText] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [reasonText, setReasonText] = useState("");

  const { data: apiDetail, isLoading, isError } = useGetStaffInvestorKycQuery(submissionId, {
    skip: !submissionId,
  });

  const [approveKyc, { isLoading: isApproving }] = useApproveStaffInvestorKycMutation();
  const [rejectKyc, { isLoading: isRejecting }] = useRejectStaffInvestorKycMutation();
  const [underReviewKyc, { isLoading: isMarkingReview }] = useUnderReviewStaffInvestorKycMutation();
  const [requestResubmission, { isLoading: isRequestingResubmit }] =
    useRequestResubmissionStaffInvestorKycMutation();
  const [addNote, { isLoading: isAddingNote }] = useAddStaffInvestorKycNoteMutation();

  const submission = useMemo(() => mapStaffKycDetail(apiDetail), [apiDetail]);

  const previousSubmissions = useMemo(() => {
    if (!submission?.previousSubmissionIds?.length) return [];
    return submission.previousSubmissionIds.map((id) => ({ id, submittedAt: null, status: "previous" }));
  }, [submission]);

  if (isLoading) {
    return <div className="py-5 text-center text-muted">Loading KYC submission…</div>;
  }

  if (!submission || isError) {
    return <Navigate to="/super_admin/investor_portal/investors/kyc" replace />;
  }

  const nameMatch = submission.legalName?.toLowerCase() === submission.portalName?.toLowerCase();
  const dobMatch = submission.dateOfBirth === submission.portalDateOfBirth;
  const staffNotes = (submission.notes || []).map((n) => ({
    id: n.id,
    text: n.note,
    staffName: n.staffName,
    at: n.createdAt,
  }));

  const actionLoading = isApproving || isRejecting || isMarkingReview || isRequestingResubmit;

  const closeConfirm = () => {
    setConfirm(null);
    setReasonText("");
  };

  const runApiAction = async (apiAction, reason = "") => {
    try {
      if (apiAction === "approve") await approveKyc(submission.id).unwrap();
      if (apiAction === "under_review") await underReviewKyc(submission.id).unwrap();
      if (apiAction === "reject") {
        await rejectKyc({ id: submission.id, reason: reason.trim() }).unwrap();
      }
      if (apiAction === "resubmit") {
        await requestResubmission({ id: submission.id, reason: reason.trim() }).unwrap();
      }
      toast.success("KYC updated");
      closeConfirm();
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Action failed.");
    }
  };

  const openConfirm = (action) => {
    const apiAction = resolveApiAction(action);
    if (!apiAction) {
      toast.error("This action is not available.");
      return;
    }

    const needsReason = apiAction === "reject" || apiAction === "resubmit";
    const isDestructive = apiAction === "reject";

    setReasonText("");
    setConfirm({
      apiAction,
      needsReason,
      title: action.label,
      message: needsReason
        ? `Provide a reason to ${action.label.toLowerCase()} for ${submission.applicantName}.`
        : `Are you sure you want to ${action.label.toLowerCase()} for ${submission.applicantName}?`,
      confirmText:
        apiAction === "approve"
          ? "Yes, approve"
          : apiAction === "reject"
            ? "Yes, reject"
            : apiAction === "resubmit"
              ? "Yes, request resubmission"
              : "Yes, continue",
      confirmButtonClass: isDestructive ? "bg-danger text-white" : "bg-primary1 text-white",
      reasonPlaceholder:
        apiAction === "reject"
          ? "Rejection reason (required)"
          : "Reason for resubmission (required)",
    });
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    if (confirm.needsReason && !reasonText.trim()) {
      toast.error("A reason is required.");
      return;
    }
    await runApiAction(confirm.apiAction, reasonText);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addNote({ id: submission.id, note: noteText.trim() }).unwrap();
      setNoteText("");
      toast.success("Note added");
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Could not add note.");
    }
  };

  return (
    <div className="pb-4">
      <InvestorPortalDetailHeader
        title="KYC review"
        highlight={submission.applicantName}
        onBack={() => navigate("/super_admin/investor_portal/investors/kyc")}
      />

      <div className="d-flex flex-wrap gap-2 mb-3">
        <KycReviewStatusBadge status={submission.status} />
        <ParticipantTypeBadge type={submission.accountType} />
        <KycMatchIndicator matchResult={submission.matchResult} />
      </div>

      <div className="row g-4">
        <div className="col-lg-8">
          <DetailSection title="Applicant profile">
            <DetailFieldList>
              <DetailField label="Account type" value={submission.accountType?.replace(/_/g, " ")} />
              <DetailField label="Email" value={submission.email} />
              <DetailField label="Phone" value={submission.phone} />
              <DetailField label="Submitted legal name" value={submission.legalName} />
              <DetailField label="Portal profile name" value={submission.portalName} />
              <DetailField label="Submitted date of birth" value={submission.dateOfBirth} />
              <DetailField label="Portal date of birth" value={submission.portalDateOfBirth} />
              <DetailField label="NIN (masked)" value={submission.ninMasked} />
              <DetailField label="Submitted" value={moment(submission.submittedAt).format("DD-MMM-YYYY HH:mm")} />
              <DetailField label="Reviewed by" value={submission.reviewedBy || "—"} />
              <DetailField label="Rejection reason" value={submission.rejectionReason || "—"} />
              <DetailField label="Resubmission reason" value={submission.resubmissionReason || "—"} />
            </DetailFieldList>
          </DetailSection>

          <DetailSection title="Field match indicators">
            <div className="d-flex flex-column gap-2">
              <FieldMatchIndicator matched={nameMatch} label="Legal name vs portal name" />
              <FieldMatchIndicator matched={dobMatch} label="Submitted DOB vs portal DOB" />
            </div>
            <p className="text-muted small mt-3 mb-0">{submission.providerResponse}</p>
          </DetailSection>

          <DetailSection title="Status timeline">
            <KycReviewTimeline entries={submission.timeline} />
          </DetailSection>

          {previousSubmissions.length > 0 && (
            <DetailSection title="Previous submissions">
              <ul className="list-unstyled mb-0 small">
                {previousSubmissions.map((s) => (
                  <li key={s.id} className="py-1">
                    {s.submittedAt ? moment(s.submittedAt).format("DD-MMM-YYYY") : "Previous"} —{" "}
                    {s.status?.replace(/_/g, " ")}
                  </li>
                ))}
              </ul>
            </DetailSection>
          )}
        </div>

        <div className="col-lg-4">
          <CardComp cardClassName="border-0 shadow-sm mb-3">
            <h6 className="investor-portal-section-title">Review actions</h6>
            <div className="d-flex flex-column gap-2">
              {getKycReviewActions(submission.status).map((a) => (
                <InvestorPortalActionButton
                  key={a.label}
                  btnText={a.label}
                  variant={a.variant || "outline"}
                  fullWidth
                  loading={actionLoading}
                  disabled={actionLoading}
                  onClick={() => openConfirm(a)}
                />
              ))}
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm">
            <h6 className="investor-portal-section-title">Reviewer notes</h6>
            {staffNotes.length === 0 ? (
              <p className="text-muted small">No notes yet.</p>
            ) : (
              <ul className="list-unstyled small mb-3">
                {staffNotes.map((n) => (
                  <li key={n.id} className="py-2 border-bottom">
                    <p className="mb-1">{n.text}</p>
                    <span className="text-muted">
                      {n.staffName} · {moment(n.at).format("DD-MMM-YY HH:mm")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <textarea
              className="form-control form-control-sm mb-2"
              rows={2}
              placeholder="Add staff note"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <InvestorPortalActionButton
              btnText="Add note"
              variant="primary"
              fullWidth
              loading={isAddingNote}
              disabled={isAddingNote}
              onClick={handleAddNote}
            />
          </CardComp>
        </div>
      </div>

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        title={confirm?.title}
        message={
          confirm ? (
            <div>
              <p className="mb-0">{confirm.message}</p>
              {confirm.needsReason ? (
                <textarea
                  className="form-control investor-portal-note-input mt-3"
                  rows={3}
                  placeholder={confirm.reasonPlaceholder}
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  disabled={actionLoading}
                />
              ) : null}
            </div>
          ) : (
            ""
          )
        }
        confirmText={confirm?.confirmText || "Yes, continue"}
        cancelText="Cancel"
        confirmButtonClass={confirm?.confirmButtonClass || "bg-primary1 text-white"}
        isLoading={actionLoading}
      />
    </div>
  );
}

export default withInvestorPortalGuard(KycReviewDetailPage, "investorPortal");
